import { Accreditation } from 'common-front/src/accreditations/v2/accreditation';
import { Checkbox, ICheckboxState } from 'common-front/src/designSystem/components/checkbox';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { HeaderCell } from 'common/src/designSystem/components/table/headerCell';
import { HeaderRow } from 'common/src/designSystem/components/table/headerRow';
import { Table } from 'common/src/designSystem/components/table/table';
import { EventAccreditationsFragment } from 'common/src/generated/types';
import { ICreateUpdateDelegationValues } from 'common/src/input/delegationInput';
import { sortedSlots } from 'common/src/vo/accreditationSlot';
import { difference, uniq } from 'lodash-es';
import * as React from 'react';
import { DelegationAccreditationActions } from './delegationAccreditationActions';
import { DelegationAccreditationSlot } from './delegationAccreditationSlot';

interface IDelegationAccreditationProps {
    accreditation: EventAccreditationsFragment['accreditationsCategories'][0]['accreditations'][0];
    slots: EventAccreditationsFragment['accreditationsCategories'][0]['accreditations'][0]['slots'];
    values: ICreateUpdateDelegationValues;

    change(name: string, value: any): void;
}

export const DelegationAccreditation = (props: IDelegationAccreditationProps) => {
    const { translate } = useHeavent();
    const index = React.useMemo(
        () =>
            props.values.delegation.accreditations.findIndex(
                (a) => a.accreditationId === props.accreditation.id
            ),
        [props.values, props.accreditation]
    );
    const slots = React.useMemo(
        () => sortedSlots(props.slots.filter((s) => s.accreditationId === props.accreditation.id)),
        [props.slots, props.accreditation]
    );
    const slotsIds = React.useMemo(() => slots.map((slot) => slot.id), [slots]);
    const prefix = `delegation.accreditations[${index}]`;
    const selectedState = React.useMemo(() => {
        if (props.accreditation.hasSlots) {
            const areSlotsSelecteds = uniq(
                slots.map((slot) => props.values.selectedAccreditationsSlotsIds.includes(slot.id))
            );

            return areSlotsSelecteds.length === 2
                ? 'indeterminate'
                : areSlotsSelecteds[0]
                  ? 'checked'
                  : 'unchecked';
        } else {
            return 'unchecked';
        }
    }, [props.accreditation, props.values.selectedAccreditationsSlotsIds, slots]);
    const selectedSlotsIds = React.useMemo(
        () =>
            slotsIds.filter((slotId) =>
                props.values.selectedAccreditationsSlotsIds.includes(slotId)
            ),
        [props.values.selectedAccreditationsSlotsIds, slotsIds]
    );
    const onSelectedStateChange = React.useCallback(
        (newState: ICheckboxState) => {
            if (newState === 'checked') {
                props.change(
                    'selectedAccreditationsSlotsIds',
                    uniq(props.values.selectedAccreditationsSlotsIds.concat(slotsIds))
                );
            } else {
                props.change(
                    'selectedAccreditationsSlotsIds',
                    difference(props.values.selectedAccreditationsSlotsIds, slotsIds)
                );
            }
        },
        [props.values.selectedAccreditationsSlotsIds, props.change, slotsIds]
    );

    return (
        <Accreditation
            accreditation={props.accreditation}
            displayNumberOfSlots={true}
            displaySlots={true}
            numberOfSlots={slots.length}
            renderActions={() => (
                <DelegationAccreditationActions
                    change={props.change}
                    hasSlots={props.accreditation.hasSlots}
                    prefix={prefix}
                    selectedSlotsIds={selectedSlotsIds}
                    slotsIds={slotsIds}
                    values={props.values}
                />
            )}
        >
            <Table hideBorder={true}>
                <HeaderRow>
                    {props.accreditation.hasSlots && (
                        <HeaderCell width={48}>
                            <Checkbox state={selectedState} onClick={onSelectedStateChange} />
                        </HeaderCell>
                    )}
                    <HeaderCell>{translate('date_du_cr_neau_82295')}</HeaderCell>
                    <HeaderCell>{translate('nom_du_cr_neau_54351')}</HeaderCell>
                    <HeaderCell>{translate('accr_ditation_p_28030')}</HeaderCell>
                    <HeaderCell width={100}>{translate('Privacy')}</HeaderCell>
                    <HeaderCell width={130}>{translate('total_07173')}</HeaderCell>
                </HeaderRow>

                {slots.map((slot, slotIndex) => (
                    <DelegationAccreditationSlot
                        key={`${slot.id}-${slotIndex}`}
                        accreditationDisplay={props.accreditation.accreditationDisplay}
                        change={props.change}
                        displaySelectedCheckbox={props.accreditation.hasSlots}
                        slot={slot}
                        values={props.values}
                    />
                ))}
            </Table>
        </Accreditation>
    );
};
